/**
 * RBAC (Role-Based Access Control) Utilities
 *
 * Frontend helpers for role and permission checking
 * Based on SPEC-1 REQ-4.2, REQ-4.3
 *
 * NOTE: Direct Supabase query functions are DEPRECATED.
 * Use RBACContext (useRBAC hook) instead, which fetches from the backend
 * /api/v1/admin/me endpoint for consistent role values.
 */

import { supabase } from './supabase';
import type { Database } from './database.types';

type Profile = Database['public']['Tables']['profiles']['Row'];
type UserMembership = Database['public']['Tables']['user_memberships']['Row'];
type Role = 'user' | 'group_admin' | 'company_admin' | 'admin' | 'superadmin';

/**
 * Role hierarchy for privilege checking
 *
 * Hierarchy:
 * - user (0): Basic access to assigned units and groups
 * - group_admin (1): Can manage users and settings for specific teams/groups
 * - company_admin (2): Full access to their company, can manage all users and teams within it
 * - admin (3): Internal HT staff with elevated access across companies
 * - superadmin (4): System-wide access across all companies (internal HT only)
 */
const ROLE_HIERARCHY: Record<Role, number> = {
  user: 0,
  group_admin: 1,
  company_admin: 2,
  admin: 3,
  superadmin: 4,
};

/**
 * Roles that administer a COMPANY rather than a single group.
 *
 * A membership scoped to a group only ever reaches that group and its
 * descendants, so pinning one of these roles to a group froze the user's access
 * at the groups that existed the day they were added — every production created
 * afterwards was invisible to them. The server stores these roles company-wide
 * (api/services/rbac.js `normalizeMembershipGroupId`, enforced by a trigger in
 * migration 20260811020000); this mirror keeps the UI honest about that.
 */
export const COMPANY_LEVEL_ROLES: readonly Role[] = ['superadmin', 'admin', 'company_admin'];

export function isCompanyLevelRole(role: string | null | undefined): boolean {
  return COMPANY_LEVEL_ROLES.includes(role as Role);
}

/**
 * Check if a role has equal or higher privilege than required role
 * This is a pure function - safe to use anywhere
 */
export function roleHasPrivilege(userRole: Role, requiredRole: Role): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole];
}

/**
 * @deprecated Use useRBAC() hook from RBACContext instead.
 * This function queries Supabase directly from the browser.
 * The RBACContext fetches from backend for consistent role values.
 *
 * Get current user's profile
 */
export async function getCurrentUserProfile(): Promise<Profile | null> {
  console.warn(
    '[DEPRECATED] getCurrentUserProfile() is deprecated. Use useRBAC() hook instead.'
  );
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', user.id)
    .single();

  if (error) {
    console.error('Error fetching user profile:', error);
    return null;
  }

  return data;
}

/**
 * @deprecated Use useRBAC() hook from RBACContext instead.
 * This function queries Supabase directly from the browser.
 * The RBACContext fetches from backend for consistent role values.
 *
 * Get current user's memberships
 */
export async function getCurrentUserMemberships(): Promise<UserMembership[]> {
  console.warn(
    '[DEPRECATED] getCurrentUserMemberships() is deprecated. Use useRBAC() hook instead.'
  );
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    console.log('[RBAC] No user found, returning empty memberships');
    return [];
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  console.log(`[RBAC] Fetching memberships for user ${user.id} on ${hostname}`);

  const { data, error } = await supabase
    .from('user_memberships')
    .select('*')
    .eq('user_id', user.id);

  if (error) {
    console.error('[RBAC] Error fetching user memberships:', error);
    return [];
  }

  console.log(
    `[RBAC] Found ${data?.length || 0} memberships:`,
    data?.map((m) => ({
      role: m.role,
      company_id: m.company_id,
      group_id: m.group_id,
    }))
  );

  return data || [];
}

/**
 * @deprecated Use useRBAC() hook from RBACContext instead.
 * This function queries Supabase directly from the browser.
 * The RBACContext fetches from backend for consistent role values.
 *
 * Check if current user has required role for a company
 */
export async function currentUserHasCompanyRole(
  companyId: string,
  requiredRole: Role = 'user'
): Promise<boolean> {
  console.warn(
    '[DEPRECATED] currentUserHasCompanyRole() is deprecated. Use useRBAC() hook instead.'
  );
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) return false;

  const { data, error } = await supabase
    .from('user_memberships')
    .select('role')
    .eq('user_id', user.id)
    .eq('company_id', companyId)
    .single();

  if (error || !data) return false;

  return roleHasPrivilege(data.role as Role, requiredRole);
}

/**
 * @deprecated Use useRBAC() hook from RBACContext instead.
 * This function queries Supabase directly from the browser.
 * The RBACContext fetches from backend /api/v1/admin/me for consistent role values.
 *
 * Check if current user is superadmin
 * Matches database is_superadmin() function logic:
 * - profiles.is_internal = true OR
 * - user_memberships.role = 'superadmin'
 */
export async function currentUserIsSuperadmin(): Promise<boolean> {
  console.warn(
    '[DEPRECATED] currentUserIsSuperadmin() is deprecated. Use useRBAC().isSuperadmin instead.'
  );
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    console.log('[RBAC] No user found, returning false for superadmin check');
    return false;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'unknown';
  console.log(`[RBAC] Checking superadmin status for user ${user.id} on ${hostname}`);

  // Check 1: profiles.is_internal = true (internal users are superadmins)
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('is_internal')
    .eq('user_id', user.id)
    .single();

  if (profileError) {
    console.log(`[RBAC] Profile query error:`, profileError);
  } else {
    console.log(`[RBAC] Profile is_internal:`, profile?.is_internal);
  }

  if (!profileError && profile?.is_internal === true) {
    console.log(`[RBAC] User is superadmin (is_internal = true)`);
    return true;
  }

  // Check 2: user_memberships.role = 'superadmin'
  const { data, error } = await supabase
    .from('user_memberships')
    .select('role, company_id, group_id')
    .eq('user_id', user.id)
    .eq('role', 'superadmin')
    .limit(1);

  if (error) {
    console.error('[RBAC] Error checking superadmin status:', error);
    return false;
  }

  const isSuperadmin = data !== null && data.length > 0;
  console.log(`[RBAC] Superadmin check result: ${isSuperadmin}`, {
    membershipsFound: data?.length || 0,
    data: data,
  });

  return isSuperadmin;
}

/**
 * @deprecated Use useRBAC() hook from RBACContext instead.
 * This function queries Supabase directly from the browser.
 * The RBACContext fetches from backend for consistent role values.
 *
 * Get all companies current user has access to
 */
export async function getCurrentUserCompanys() {
  console.warn(
    '[DEPRECATED] getCurrentUserCompanys() is deprecated. Use useRBAC().companies instead.'
  );
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) return [];

  const { data, error } = await supabase
    .from('user_memberships')
    .select(
      `
      role,
      companies (
        id,
        name,
        slug,
        is_internal,
        industry,
        logo_url,
        theme_json
      )
    `
    )
    .eq('user_id', user.id)
    .is('group_id', null); // Only company-level memberships

  if (error) {
    console.error('Error fetching user companies:', error);
    return [];
  }

  return (
    data?.map((m) => ({
      ...m.companies,
      user_role: m.role as Role,
    })) || []
  );
}

/**
 * Role display names for UI
 * Safe to use - just constants
 */
export const ROLE_LABELS: Record<Role, string> = {
  user: 'User',
  group_admin: 'Team Admin',
  company_admin: 'Company Admin',
  admin: 'Admin',
  superadmin: 'Super Admin',
};

/**
 * Role descriptions for UI
 * Safe to use - just constants
 */
export const ROLE_DESCRIPTIONS: Record<Role, string> = {
  user: 'Basic access to assigned units and groups',
  group_admin: 'Can manage users and settings for specific teams',
  company_admin: 'Full access to their company, can manage all users and teams',
  admin: 'Internal staff with elevated access across companies',
  superadmin: 'System-wide access across all companies',
};

/**
 * Get role badge color for UI
 * Safe to use - just a utility function
 */
export function getRoleBadgeColor(role: Role): string {
  const colors: Record<Role, string> = {
    user: 'bg-slate-500',
    group_admin: 'bg-blue-500',
    company_admin: 'bg-green-500',
    admin: 'bg-purple-500',
    superadmin: 'bg-red-500',
  };
  return colors[role] || 'bg-gray-500';
}
